import React, { useMemo } from 'react';
import styled from 'styled-components';

import { Tablet } from '@/styles/layout';

interface Props {
  content: string;
  hasFaq?: boolean;
  faqTitle?: string;
}

interface TocItem {
  title: string;
  padding: number;
  order?: number;
}

const DiseaseEncyclopediaTableOfContents = (props: Props) => {
  const headings = props.content.split('\n').filter((t) => t.includes('# '));
  const results: TocItem[] = useMemo(() => {
    let order = 0;
    return headings
      .filter((str) => str[0] === '#')
      .filter((str) => str.match(/#/g)?.length && (str.match(/#/g)?.length ?? 0) < 3)
      .map((item) => {
        const count = item.match(/#/g)?.length;
        let padding = 0;

        // 목차에 기본 사용될 태그가 h2이니 h3는 마진을 주어서 계층 구조 표현
        if (count && count > 2) {
          padding = count * 10;
        }

        //h2 태그일 경우만 order를 사용한다.
        if (count === 2) {
          ++order;
          return {
            title: item.split('# ')[1].replace(/`/g, '').trim(),
            padding: padding ?? 0,
            order,
          };
        }
        return {
          title: item.split('# ')[1].replace(/`/g, '').trim(),
          padding: padding ?? 0,
        };
      });
  }, [headings]);

  const resultsWithFaq = useMemo(() => {
    const baseResults = [...results];
    if (props.hasFaq && props.faqTitle) {
      const nextOrder = baseResults.filter((item) => item.order).length + 1;
      baseResults.push({
        title: props.faqTitle,
        padding: 0,
        order: nextOrder,
      });
    }
    return baseResults;
  }, [results, props.hasFaq, props.faqTitle]);

  return (
    <div className='mx-[24px] mb-[40px] flex flex-col rounded-[12px] bg-gray-40 px-[24px] pt-[32px] pb-[32px] '>
      <Title>원하는 정보 빠르게 찾기</Title>
      {resultsWithFaq.map((item) => (
        <Item key={item.title} padding={item.padding}>
          {item.order ? `${item.order}. ` : ''}
          <a href={`#${item.title}`}>{item.title}</a>
        </Item>
      ))}
    </div>
  );
};

export default DiseaseEncyclopediaTableOfContents;

const Title = styled.div`
  ${({ theme }) => theme.typography.UI18SB};
  color: ${({ theme }) => theme.color.gray600};
  margin-bottom: 16px;

  @media screen and (max-width: ${Tablet}) {
    ${({ theme }) => theme.typography.UI16SB};
  }
`;

const Item = styled.div<{ padding: number }>`
  width: 100%;
  padding-left: ${({ padding }) => padding}px;
  margin-bottom: 8px;
  ${({ theme }) => theme.typography.UI16R};
  color: ${({ theme }) => theme.color.gray500};
  position: relative;
  @media screen and (max-width: ${Tablet}) {
    ${({ theme }) => theme.typography.UI14R};
  }

  &:before {
    content: '';
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.color.gray500};
    display: ${({ padding }) => (padding ? 'block' : 'none')};
    position: absolute;
    left: 20px;
    top: 10px;
  }

  a {
    outline: none;
    color: ${({ theme }) => theme.color.gray500};
    cursor: pointer;
    text-decoration: underline;
    color: ${({ theme }) => theme.color.gray500} !important;
    &:hover {
      color: ${({ theme }) => theme.color.gray700};
    }
  }
`;
